import { openMessageAttachment } from "./openMessageAttachment";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Table, Pagination, Tag, Button, Empty, Input, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";
import Avvvatars from "avvvatars-react";
import {
  getFileAttachementByEntityId,
  getFileAttachementByName,
} from "../../api/fileAttachement";
import { toast } from "sonner";
import UpdateMessageModal from "./updateMessageModal";
import ArchiveMessageModal from "./archiveMessageModal";
import { delete_message } from "../../api/messages";
import { useSocket } from "../../core/common/context/SocketContext";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

export interface Message {
  id: string;
  title?: string;
  message: string;
  message_type: "Info" | "Urgent" | "Avertissement" | string;
  sender_id?: string;
  target_type?: string;
  message_sent?: boolean;
  draft?: boolean;
  deleted?: boolean;
  archive?: boolean;
  UserProfil?: {
    id?: string;
    firstname?: string;
    lastname?: string;
    email?: string;
  };
  MessageRecipients?: {
    id: string;
    receiver_type: "STUDENT" | "TEACHER" | "STAFF" | "PARENT" | string;
    receiverId: string;
    profil?: {
      id?: string;
      firstname?: string;
      lastname?: string;
      email?: string;
    };
  }[];
  createdAt?: string;
  attachments?: { id: string; filename: string; url?: string }[];
  unread?: boolean;
  recipients?: { receiverId: string; receiver_type: string }[];
  direction?: "sent" | "received" | string;
}

interface Props {
  messages: any[];
  isLoading: boolean;
  refresh: () => void;
}

const MessageList = ({ messages, isLoading, refresh }: Props) => {
  const { socket } = useSocket();

  const [myMessages, setMyMessages] = useState<Message[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [showRecipients, setShowRecipients] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const user = useSelector((state: any) => state.auth.user);

  useEffect(() => {
    if (!socket) return;

    socket.on("new_message", (data: any) => {
      refresh();
    });

    return () => {
      socket.off("new_message");
    };
  }, [socket]);

  const columns = [
    {
      title: "Expéditeur",
      dataIndex: "from",
      key: "from",
      render: (_: any, record: Message) => {
        const name = record.UserProfil
          ? `${record.UserProfil.firstname || ""} ${record.UserProfil.lastname || ""}`.trim()
          : "Inconnu";
        return (
          <div className="d-flex gap-2 align-items-center">
            <Avvvatars
              value={`${record?.UserProfil?.firstname?.charAt(0) || ""}${
                record?.UserProfil?.lastname?.charAt(0) || ""
              }`}
              style="character"
            />
            <div className="min-w-0"><div className={record.unread ? "fw-bold" : "fw-semibold"}>{name}</div>
              <small className="text-muted">{record.direction === "sent" ? "Message envoyé" : record.UserProfil?.email}</small></div>
          </div>
        );
      },
      width: 230,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (text: string, record: Message) => (
        <div className="message-row-content">
          <div className={record.unread ? "message-row-title fw-bold" : "message-row-title"}>
            {record.title || "Sans objet"}
          </div>
          <div className="message-row-preview">{text.length > 90 ? `${text.slice(0, 90)}…` : text}</div>
          <div className="mt-1 d-flex gap-1">
            <Tag
              color={
                record.message_type === "Urgent"
                  ? "red"
                  : record.message_type === "Avertissement"
                    ? "orange"
                    : "blue"
              }
            >
              {record.message_type === "Info" ? "Normale" : record.message_type}
            </Tag>
            {record.draft && <Tag>Brouillon</Tag>}
            {record.archive && <Tag>Archivé</Tag>}
          </div>
        </div>
      ),
      ellipsis: true,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string, record: Message) => (
        <span className={record.unread ? "text-dark fw-semibold" : "text-dark"}>
          {text ? <><div>{dayjs(text).format("DD MMM YYYY")}</div><small className="text-muted">{dayjs(text).format("HH:mm")}</small></> : "-"}
        </span>
      ),
      width: 125,
    },
  ];

  const filteredMessages = myMessages.filter((m) => {
    // const isReceived = m?.direction ? m.direction !== "sent" : true;
    // if (!isReceived) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const senderName =
      `${m?.UserProfil?.firstname || ""} ${m?.UserProfil?.lastname || ""}`.toLowerCase();
    const email = (m?.UserProfil?.email || "").toLowerCase();
    const content = (m?.message || "").toLowerCase();
    const title = (m?.title || "").toLowerCase();
    const type = (m?.message_type || "").toLowerCase();
    const target = (m?.target_type || "").toLowerCase();
    return (
      senderName.includes(q) ||
      email.includes(q) ||
      content.includes(q) || title.includes(q) ||
      type.includes(q) ||
      target.includes(q)
    );
  });

  const paginatedMessages = filteredMessages.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const fetchMessagesDocsAndRecipientInfos = async () => {
    try {
      const response = await getFileAttachementByEntityId(
        selectedMessage?.id,
        "Message",
        buildPermissionContext(Resource.MESSAGE, Action.READ),
      );
      setFiles(response.data);
    } catch (error: any) {
      // console.log("erreur de chargement des documentss : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleGetAssignementFile = async (fileName: string) => {
    try {
      await openMessageAttachment(() => getFileAttachementByName(
        "Message",
        fileName,
        buildPermissionContext(Resource.MESSAGE, Action.READ),
      ));
    } catch (error: any) {
      // console.log("Erreur lors du téléchargement du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    setMyMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (!selectedMessage) return;
    fetchMessagesDocsAndRecipientInfos();
  }, [selectedMessage]);

  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  //Traitement en alert de la demande de réinitialisation de mot de passe
  const showConfirmationAlert = (messageId: string) => {
    MySwal.fire({
      title: "Supprimer ce message ?",
      text: `Êtes-vous sûr de vouloir supprimer ce message ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Supprimer",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête d'envoi de mail
          await delete_message(messageId);
          MySwal.fire({
            title: "Message supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          refresh();
          setDetailVisible(false);
          setSelectedMessage(null);
        } catch (error) {
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text: "Échec de la suppression du message. Veuillez réessayer plus tard.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  return (
    <>
      {!detailVisible ? (
        <div className="col-12">
          <div className="card message-list-card">
            <div className="card-body p-0">
              <div className="message-list-header">
                <div>
                  <h5 className="mb-1">Messages</h5>
                  <span className="text-muted small">{filteredMessages.length} message{filteredMessages.length > 1 ? "s" : ""}</span>
                </div>
                <div className="message-list-tools">
                        <Input
                          allowClear
                          prefix={<i className="ti ti-search text-muted" />}
                          placeholder="Rechercher par objet, contenu ou expéditeur"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                          }}
                          style={{ width: 340 }}
                        />
                  <Tooltip title="Actualiser"><Button icon={<i className="ti ti-refresh" />} onClick={refresh} /></Tooltip>
                </div>
              </div>
              <div className="email-content">
                <div className="table-responsive">
                  {isLoading ? (
                    <div className="p-4"><Skeleton active paragraph={{ rows: 6 }} /></div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={paginatedMessages}
                      rowKey="id"
                      pagination={false}
                      className="table-inbox table-hover"
                      rowClassName="clickable-row"
                      onRow={(record) => ({
                        onClick: () => {
                          setSelectedMessage(record as Message);
                          setDetailVisible(true);
                        },
                      })}
                      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={searchQuery ? "Aucun message ne correspond à votre recherche" : "Aucun message dans cette section"} /> }}
                    />
                  )}
                </div>
                <div className="message-list-pagination">
                  <div className="d-flex align-items-center">
                    <span className="me-2 text-muted">Par page</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>05</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <Pagination
                    current={currentPage}
                    total={filteredMessages.length}
                    pageSize={pageSize}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="col-12">
          <div className="card h-100 message-detail-card">
            <div className="card-body h-100 p-0">
              <div className="message-detail-toolbar">
                <button
                  className="btn btn-light"
                  onClick={() => {
                    setDetailVisible(false);
                    setSelectedMessage(null);
                  }}
                >
                  <i className="fas fa-arrow-left" />
                </button>
                <div className="d-flex align-items-center message-detail-sender">
                  <span className="avatar avatar-sm rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2">
                    {`${selectedMessage?.UserProfil?.firstname?.[0] || ""}${selectedMessage?.UserProfil?.lastname?.[0] || ""}`.toUpperCase() ||
                      "U"}
                  </span>

                  <div>
                    <div className="fw-semibold">
                      {`${selectedMessage?.UserProfil?.firstname || ""} ${selectedMessage?.UserProfil?.lastname || ""}`.trim() ||
                        "Utilisateur"}
                    </div>
                    <div className="text-muted small">
                      {selectedMessage?.UserProfil?.email || ""}
                    </div>
                  </div>
                </div>
                <div className="ms-auto message-detail-actions">
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <Tag
                      color={
                        selectedMessage?.message_type === "Urgent"
                          ? "red"
                          : selectedMessage?.message_type === "Avertissement"
                            ? "orange"
                            : "blue"
                      }
                    >
                      {selectedMessage?.message_type}
                    </Tag>
                    {selectedMessage?.sender_id &&
                      user?.id &&
                      selectedMessage?.sender_id === user.id &&
                      !selectedMessage?.deleted &&
                      !selectedMessage?.archive && (
                        <Button
                          size="small"
                          onClick={() => setShowUpdateModal(true)}
                        >
                          Modifier
                        </Button>
                      )}
                    {selectedMessage?.sender_id &&
                      user?.id &&
                      selectedMessage?.sender_id === user.id &&
                      !selectedMessage?.deleted &&
                      !selectedMessage?.draft &&
                      !selectedMessage?.archive && (
                        <Button
                          className="btn-warning"
                          size="small"
                          style={{
                            backgroundColor: "#ffc107",
                            borderColor: "#ffc107",
                            color: "#212529",
                          }}
                          onClick={() => setShowArchiveModal(true)}
                        >
                          Archiver
                        </Button>
                      )}
                    {selectedMessage?.sender_id &&
                      user?.id &&
                      selectedMessage?.sender_id === user.id &&
                      !selectedMessage?.deleted &&
                      !selectedMessage?.archive && (
                        <Button
                          className="btn-danger"
                          size="small"
                          style={{
                            backgroundColor: "#dc3545",
                            borderColor: "#dc3545",
                            color: "#fff",
                          }}
                          onClick={() =>
                            showConfirmationAlert(selectedMessage.id)
                          }
                        >
                          Supprimer
                        </Button>
                      )}
                  </div>
                </div>
              </div>
              <div className="message-detail-body">
              <div className="message-detail-heading">
                <div>
                  <h4>{selectedMessage?.title || "Sans objet"}</h4>
                  <div className="text-muted small">
                    {selectedMessage?.direction === "sent" ? "Envoyé" : "Reçu"} le {selectedMessage?.createdAt
                      ? dayjs(selectedMessage.createdAt).format("DD MMMM YYYY [à] HH:mm") : "-"}
                  </div>
                </div>
                <Tag color={selectedMessage?.message_type === "Urgent" ? "red" : selectedMessage?.message_type === "Avertissement" ? "orange" : "blue"}>
                  {selectedMessage?.message_type === "Info" ? "Priorité normale" : selectedMessage?.message_type}
                </Tag>
              </div>
              <div className="message-detail-content">
                {selectedMessage?.message?.split("\n").map((line: string, index: number) => <p key={index}>{line || <br />}</p>)}
              </div>
              <div className="message-detail-section">
                <h6><i className="ti ti-paperclip me-2" />Pièces jointes <span className="text-muted">({files.length})</span></h6>
                {files.length > 0 ? (
                  <div className="message-attachments">
                    {files.map((f) => (
                      <div key={f.id} className="message-attachment">
                        <span className="message-attachment-icon"><i className="ti ti-file" /></span>
                        {f.url ? (
                          <a href={f.url} target="_blank" rel="noreferrer">
                            {f.filename}
                          </a>
                        ) : (
                          <span
                            onClick={() => {
                              handleGetAssignementFile(f.filename);
                            }}
                            style={{
                              cursor: "pointer",
                              color: "inherit",
                              textDecoration: "underline",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.color = "#1890ff")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.color = "inherit")
                            }
                          >
                            {f.filename}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">Aucun fichier attaché</div>
                )}
              </div>
              {/* Header with switch to show/hide recipients */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0"><i className="ti ti-users me-2" />Destinataires
                  {selectedMessage?.MessageRecipients?.length ? <span className="text-muted"> ({selectedMessage.MessageRecipients.length})</span> : null}</h6>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showRecipients"
                    checked={showRecipients}
                    onChange={(e) => setShowRecipients(e.target.checked)}
                  />
                  <label
                    className="form-check-label small"
                    htmlFor="showRecipients"
                  >
                    {showRecipients ? "Masquer" : "Afficher"}
                  </label>
                </div>
              </div>

              {/* Recipients cards list - only shown if switch is ON */}
              {showRecipients && (
                <>
                  {selectedMessage?.MessageRecipients &&
                  selectedMessage?.MessageRecipients.length > 0 ? (
                    <div className="row g-2">
                      {selectedMessage.MessageRecipients.map(
                        (r: any, _idx: any) => {
                          const color =
                            r.receiver_type === "STUDENT"
                              ? "green"
                              : r.receiver_type === "TEACHER"
                                ? "cyan"
                                : r.receiver_type === "STAFF"
                                  ? "purple"
                                  : r.receiver_type === "PARENT"
                                    ? "gold"
                                    : "blue";
                          return (
                            <div
                              key={r.id + _idx}
                              className="col-md-6 col-lg-4"
                            >
                              <div className="recipient-chip">
                                <span className="recipient-chip-icon"><i className={`ti ${r.receiver_type === "STUDENT" ? "ti-school" : r.receiver_type === "TEACHER" ? "ti-chalkboard" : r.receiver_type === "PARENT" ? "ti-users" : "ti-user"}`} /></span>
                                <div><div className="fw-semibold">{r.profil ? `${r.profil.firstname || ""} ${r.profil.lastname || ""}`.trim() : ({ STUDENT: "Étudiant", TEACHER: "Enseignant", STAFF: "Personnel", PARENT: "Parent" } as Record<string, string>)[r.receiver_type] || "Destinataire"}</div>
                                  <small className="text-muted">{r.profil?.email || `Identifiant : ${String(r.receiverId).slice(0, 8)}…`}</small></div>
                                <Tag color={color} className="ms-auto">{({ STUDENT: "Étudiant", TEACHER: "Enseignant", STAFF: "Personnel", PARENT: "Parent" } as Record<string, string>)[r.receiver_type] || r.receiver_type}</Tag>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  ) : (
                    <div className="text-muted">
                      {selectedMessage?.target_type
                        ? `${selectedMessage.target_type}`
                        : "Aucun destinataire"}
                    </div>
                  )}
                </>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .table-inbox .ant-table-row {
          cursor: pointer;
        }
      `}</style>
      {showUpdateModal && (
        <UpdateMessageModal
          visible={showUpdateModal}
          onCancel={() => setShowUpdateModal(false)}
          messageData={selectedMessage}
          onUpdated={() => {
            setShowUpdateModal(false);
            refresh();
            setDetailVisible(false);
            setSelectedMessage(null);
          }}
        />
      )}
      {showArchiveModal && (
        <ArchiveMessageModal
          visible={showArchiveModal}
          onCancel={() => setShowArchiveModal(false)}
          messageData={selectedMessage}
          messagePreview={selectedMessage?.message}
          onConfirm={() => {
            refresh();
            setDetailVisible(false);
            setSelectedMessage(null);
          }}
        />
      )}
    </>
  );
};

export default MessageList;
